import { PetDiagnosisModel, DiagnosisModel, EstimateModel, EstimateSalesItemModel, HospitalScheduleVetTechModel, HospitalScheduleVeterinarianModel, IBlobSasModel, IConsentForm, IExamCategoryModel, IPhotoRequest, IVisitAnesthesiaFormPostSurgeryModel, IVisitAnesthesiaFormPreOpModel, IVisitAnesthesiaFormSurgeryModel, IWellnessExamCategory, IWellnessExamForm, IWellnessExamSalesItem, InactivateVisitReminderRequest, LabOrderModel, PhotoModel, VeterinarianModel, VisitAnesthesiaFormModel, VisitAnesthesiaFormPostSurgeryVitalModel, VisitAnesthesiaFormSurgeryVitalModel, VisitModel, VisitNoteModel, VisitSalesItemModel, VisitSectionVersionModel, VisitUpdateRequest, WellnessExamForm, schedule, VisitPetDiagnosisStatusUpdateRequest } from "@common/models";
import { AnesthesiaSectionTypes, AuthOperationRoleNames, BlobEntityType, BusinessLineEnum, ConsentType, ExamLockedStatus, HospitalScheduleDateRangeType, IdexxLabOrderStatus, MimeType, ReservationAssignmentStatuses, SalesItemType, Traits, VisitCompletionType, VisitSectionTypes, VisitStatus } from "@common/models/Enums";
import { HospitalPrescriptionModel } from "@common/models/HospitalPrescriptionModel";
import { HospitalService, LabOrderService, PetService, SalesItemService, SalesPackageService, SalesPlanService, VeterinarianService, VisitAnesthesiaFormService, VisitService } from "@common/services";
import { AzureBlobStore, NotificationStoreInstance, PetDetailsStore } from "@common/stores";
import { ObjectUtility, http, sortByInactiveStatusAndLastName } from "@common/utils";
import { ExamMessageHub } from "@hospital/stores/Hubs/ExamMessageHub";
import { SalesBuilderSearchStore } from "@hospital/stores/SalesBuilderSearchStore";
import { SalesStore } from "@hospital/stores/SalesStore";
import { defaultTo, find, get, isEmpty, isUndefined, orderBy } from "lodash";
import { DateTime } from "luxon";
import { action, computed, flow, makeObservable, observable, runInAction } from "mobx";
import { v4 as uuid } from "uuid";
export interface SectionUpdateResult {
  section: VisitSectionTypes;
  success: boolean;
}
export interface AnesthesiaSectionUpdateResult {
  section: AnesthesiaSectionTypes;
  success: boolean;
}

export class ExamStore extends SalesStore {
  petDetailsStore: PetDetailsStore;
  messageHub: ExamMessageHub;
  @observable scheduledVets: HospitalScheduleVeterinarianModel[] = [];
  @observable vetTechs: HospitalScheduleVetTechModel[] = [];
  @observable vetAssignedToVisit: VeterinarianModel | undefined = undefined;
  @observable visit: VisitModel | undefined = undefined;
  @observable addEstimateToVisitErrors: EstimateSalesItemModel[] = [];
  @observable estimateBuilderSearch: SalesBuilderSearchStore = new SalesBuilderSearchStore(this);
  @observable sectionUpdateResult: SectionUpdateResult = { section: VisitSectionTypes.None, success: false };
  @observable anesthesiaSectionUpdateResult: AnesthesiaSectionUpdateResult = { section: AnesthesiaSectionTypes.None, success: false };
  @observable visitSectionVersions: VisitSectionVersionModel[] = [];
  @observable visitLabOrders: LabOrderModel[] = [];
  @observable visitAnesthesiaForms: VisitAnesthesiaFormModel[] = [];
  @observable anesthesiaMedicationSalesItems: IWellnessExamSalesItem[] = [];
  @observable currentLabOrderId: number = 0;
  @observable selectedIvlsDeviceId: string | null = null;
  @observable timeZone: string = '';


  constructor(petDetailsStore: PetDetailsStore, timeZone: string) {
    super();

    this.petDetailsStore = petDetailsStore;
    this.messageHub = new ExamMessageHub(this);
    this.timeZone = timeZone;
    makeObservable(this, undefined);
  }

  dispose = () => this.messageHub.dispose();

  // Determine if visit's consents have opt-in to audio recording selected
  @computed get isVisitOptInToAudioRecording(): boolean {
    return this.visit?.consents.some(c => c.visitConsentClient?.optInToAudioRecording) ?? false;
  }

  // Selected Doctor
  @computed get getExamDoctor(): HospitalScheduleVeterinarianModel | undefined {
    return this.scheduledVets.find(v => v.veterinarianId === this.visit?.veterinarianId);
  }

  // Selected Vet Tech/Assistant
  @computed get getExamVetTech(): HospitalScheduleVetTechModel | undefined {
    return this.vetTechs.find(v => v.userId === this.visit?.dispensingAuthUserId);
  }

  @computed get examCategories(): IExamCategoryModel[] {
    return orderBy(
      this.visit?.examResults.map(er => er.examCategory),
      "sortOrder"
    );
  }

  /** NOTE: these form data will eventually be populated from Visit/VisitWellnessExam/ExamResult data */
  @computed get formData(): WellnessExamForm | undefined {
    return this.visit ? new WellnessExamForm(this.visit, this.timeZone) : undefined;
  }

  /**
   * Determines if the Exam is a Vet Tech Exam by checking the Reservation Assignment Status for Assigned_Technician
   */
  @computed get isVetTechExam(): boolean {
    return ReservationAssignmentStatuses.Assigned_Technician === this.visit?.reservation?.reservationAssignmentStatusId;
  }

  @computed get visitLockStatus(): ExamLockedStatus {
    if (this.visitIsAborted || this.hoursSinceFinalized > 48) {
      return ExamLockedStatus.ExamLevelOnly;
    } else if (this.hoursSinceFinalized > 24 || this.visitIsFinalizedComplete) {
      return ExamLockedStatus.ExamNotesAreAllowed;
    } else {
      return ExamLockedStatus.NoLock;
    }
  }

  @computed get visitIsSigned(): boolean {
    return this.visit?.veterinarianSignaturePhotoId !== undefined;
  }

  @computed get canFinalizeVisit(): boolean {
    return this.vetAssignedToVisit?.activeSignature?.signature !== undefined || this.formData!.signature.vetClinicSignature !== undefined;
  }
  @action.bound CanAbortVisit = (isAdmin: boolean) => {
    if (isAdmin) {
      return [VisitStatus.In_Progress, VisitStatus.Admitted, VisitStatus.Finalized].includes(this.visit?.visitStatusId!);
    }
    else {
      return [VisitStatus.In_Progress, VisitStatus.Admitted].includes(this.visit?.visitStatusId!);
    }
  }

  @action getVetAssignedToVisitByVetId = async (vetId: number) => {
    const updatedVet = await VeterinarianService.getVeterinarianByVetId(vetId);
    runInAction(() => {
      this.vetAssignedToVisit = updatedVet;
    });
  }
  @computed get visitIsAborted(): boolean {
    return [VisitStatus.Check_Out].includes(this.visit?.visitStatusId ?? 0) && VisitCompletionType.visitIsAborted(this.visit?.visitCompletionTypeId);
  }

  @computed get visitIsFinalized(): boolean {
    return [VisitStatus.Finalized, VisitStatus.Paid, VisitStatus.Check_Out].includes(this.visit?.visitStatusId ?? 0);
  }

  @computed get visitIsFinalizedComplete(): boolean {
    return this.visitIsFinalized && this.visit?.visitEvent.finalizedStart !== undefined && this.visit?.visitEvent.finalizedComplete !== undefined && this.visit?.visitEvent.finalizedComplete > this.visit?.visitEvent.finalizedStart!;
  }

  @computed get hoursSinceFinalized(): number {
    return this.visitIsFinalized ? DateTime.utc().diff(this.visit!.visitEvent.finalizedStart!).as("hours") : 0;
  }

  @computed get visitSuspenseTag(): string {
    return this.visitLockStatus == ExamLockedStatus.NoLock ? VisitService.tags.updateVisit : VisitService.tags.saveVisitFollowupNotes;
  }

  @computed get currentLabOrder(): LabOrderModel | undefined {
    // This will be used to get the first lab order that is either created or submitted
    return this.visitLabOrders.find(l => l.orderStatus !== IdexxLabOrderStatus.CANCELLED) ?? undefined;
  }

  @action.bound clearAddEstimateToVisitErrors() {
    this.addEstimateToVisitErrors = [];
  }

  @action.bound setSelectedIvlsDeviceId(device: string | null) {
    this.selectedIvlsDeviceId = device;
  }

  public loadScheduledVets = flow(function* (this: ExamStore, hospitalId: number) {
    if (this.visit === undefined) return;
    const date = this.visit.visitDate!;
    var schedule: schedule.ProcessedHospitalScheduleModel = yield HospitalService.getHospitalSchedules(hospitalId, date, HospitalScheduleDateRangeType.Day);
    this.scheduledVets = schedule.getVeterinarians(hospitalId, { day: date, isScheduled: true, hasLicense: true, hasAccess: true });
    this.vetTechs = sortByInactiveStatusAndLastName(schedule.vetTechs ?? []);

    // Scheduled vets do not have their active signature included - if we include that later on this extra fetch can be removed
    if (this.visit !== undefined) {
      this.updateScheduledVet(this.visit?.veterinarianId);
    }
  });

  @action updateScheduledVet = (veterinarianId?: number) => {
    const assignedVet = this.scheduledVets.find(
      (vet) => vet.veterinarianId === veterinarianId
    );
    if (!assignedVet) return;
    if (
      this.vetAssignedToVisit &&
      this.vetAssignedToVisit.veterinarianId === assignedVet.veterinarianId
    ) {
      return;
    }
    this.vetAssignedToVisit = { ...assignedVet } as unknown as VeterinarianModel;

  };

  public getVisit = flow(function* (this: ExamStore, visitId: number) {
    this.visit = yield VisitService.getVisit(visitId)
      .then(this.syncVisitSectionVersions)
      .then((visit: VisitModel) => {
        return runInAction(() => {
          this.visitLabOrders = visit.labOrders;
          return visit;
        });
      });
  });

  @action.bound syncVisitSectionVersions = (visit: VisitModel): VisitModel => {
    this.visitSectionVersions = visit.visitSectionVersions;
    return visit;
  };

  @action.bound sectionUpdateSucceeded = <T>(section: VisitSectionTypes, successMessage?: string) => {
    return action((value: T) => {
      //you need to wrap callbacks in actions or the callback won't be in the action context anymroe.
      this.sectionUpdateResult = { section, success: true };
      if (successMessage) NotificationStoreInstance.success(successMessage);
      return value;
    });
  };

  @action.bound sectionUpdateFailed = <T>(section: VisitSectionTypes, errorMessage: string, defaultValue: T) => {
    this.sectionUpdateResult = { section, success: false };
    return NotificationStoreInstance.onError(errorMessage, defaultValue);
  };

  @action.bound anesthesiaSectionUpdateSucceeded = <T>(section: AnesthesiaSectionTypes, successMessage?: string) => {
    return action((value: T) => {
      //you need to wrap callbacks in actions or the callback won't be in the action context anymroe.
      this.anesthesiaSectionUpdateResult = { section, success: true };
      if (successMessage) NotificationStoreInstance.success(successMessage);
      return value;
    });
  };

  @action.bound anesthesiaSectionUpdateFailed = <T>(section: AnesthesiaSectionTypes, errorMessage: string, defaultValue: T) => {
    this.anesthesiaSectionUpdateResult = { section, success: false };
    return NotificationStoreInstance.onError(errorMessage, defaultValue);
  };

  public getSalesItemsForVisit = flow(function* (this: ExamStore) {
    const visit = this.visit;
    const unfilteredSalesItems =
      visit == undefined
        ? []
        : yield SalesItemService.fetchSalesItems(visit.clinicId, visit.visitDate!)
          .then(items => items.salesItems.filter(i => i.speciesId == null || i.speciesId == visit.pet.speciesId))
          .catch(NotificationStoreInstance.onError("Failed to fetch visit sales items", this.salesItems));
    if (_.isArray(unfilteredSalesItems)) {
      const userAddedSalesItems = this.filterToSalesItemsThatCanBeAddedByUsers(unfilteredSalesItems);
      this.salesItems = this.mapSalesItemsForVisitBuilder(userAddedSalesItems);

      const anesthesiaMedicationSalesItems = this.filterToSalesItemsThatIsAnesethieaMedication(unfilteredSalesItems);
      this.anesthesiaMedicationSalesItems = this.mapSalesItemsForVisitBuilder(anesthesiaMedicationSalesItems);
    }

    // Sales plans are not clinic-date aware like sales items and packages. We must fetch plans
    // after getting sales items and filter out any plan items that aren't available in this clinic-date.
    this.salesPlans =
      visit == undefined
        ? []
        : yield SalesPlanService.getAllSalesPlans()
          // only global/hospital plans
          .then(items => items.filter(i => i.businessLineId === BusinessLineEnum.Global || i.businessLineId == BusinessLineEnum.Hospital))
          // only plans for this species
          .then(items => items.filter(i => i.speciesId == null || i.speciesId == visit.pet.speciesId))
          // only plans contents with corresponding sales items for this clinic-day.
          .then(item =>
            item.map(i => {
              return { ...i, contents: i.contents.filter(c => this.salesItems.some(si => si.salesItemOptionId === c.salesItemOptionId)) };
            })
          )
          .catch(NotificationStoreInstance.onError("Failed to fetch sales plans", this.salesPlans));
  });

  public getSalesPackagesForVisit = flow(function* (this: ExamStore) {
    const visit = this.visit;
    this.salesPackages =
      visit == undefined
        ? []
        : yield SalesPackageService.getClinicSalesPackages(visit.clinicId, visit.visitDate!)
          .then(items => items.filter(i => i.speciesId == null || i.speciesId == visit.pet.speciesId))
          .catch(NotificationStoreInstance.onError("Failed to fetch visit sales items", this.salesPackages));
  });

  public updateAssignmentSection = flow(function* (this: ExamStore, formData: WellnessExamForm, sectionType: VisitSectionTypes, sectionVersion: string) {
    if (!_.isUndefined(this.visit)) {
      const updateRequest = ExamStore.ParseVisitUpdateRequest(this.visit, formData, sectionType, sectionVersion);
      this.visit = yield VisitService.updateVisit(updateRequest)
        .then(visit => {
          runInAction(() => {
            var currentPet = this.petDetailsStore.pet;
            currentPet.weight = visit.pet.weight;
            this.petDetailsStore.setPet({ ...currentPet });
          });
          return visit;
        })
        .then((visit) => // optionally update visit vet
          sectionType === VisitSectionTypes.Assignment && visit.veterinarianId != formData.assignment.vetId
            ? VisitService.updateVisitVet(visit.visitId, formData.assignment.vetId, this.visit!.visitStatusId).then(result => {
              this.updateScheduledVet(formData.assignment.vetId);
              return result;
            })
            : visit
        )
        .then(this.sectionUpdateSucceeded(sectionType, `${VisitSectionTypes[sectionType]} updated.`))
        .then(this.syncVisitSectionVersions)
        .catch(this.sectionUpdateFailed(sectionType, `Failed to update ${VisitSectionTypes[sectionType]}`, this.visit));
    }
    return this.sectionUpdateResult;
  });

  public updateVisitSection = flow(function* (this: ExamStore, formData: WellnessExamForm, sectionType: VisitSectionTypes, sectionVersion: string) {
    if (this.visitLockStatus !== ExamLockedStatus.NoLock) {
      // get all notes that have been modified.
      // now that we save on a per-section basis, we don't expect that there will ever be more than one at at time.
      for (var followupNote of ExamStore.parseFollowupVisitNotes(formData) ?? []) {
        this.visit = yield VisitService.saveFollowupVisitNote(followupNote)
          .then(this.sectionUpdateSucceeded(sectionType, `${VisitSectionTypes[sectionType]} updated.`))
          .then(this.syncVisitSectionVersions)
          .catch(this.sectionUpdateFailed(sectionType, `Failed to add ${VisitSectionTypes[sectionType]} followup note`, this.visit));
      }

    } else if (!_.isUndefined(this.visit)) {
      const updateRequest = ExamStore.ParseVisitUpdateRequest(this.visit, formData, sectionType, sectionVersion);
      this.visit = yield VisitService.updateVisit(updateRequest)
        .then(this.sectionUpdateSucceeded(sectionType, `${VisitSectionTypes[sectionType]} updated.`))
        .then(visit => {
          var currentPet = this.petDetailsStore.pet;
          currentPet.weight = visit.pet.weight;
          this.petDetailsStore.setPet({ ...currentPet });
          return visit;
        })
        .then(this.syncVisitSectionVersions)
        .catch(this.sectionUpdateFailed(sectionType, `Failed to update ${VisitSectionTypes[sectionType]}`, this.visit));

    }
    return this.sectionUpdateResult;
  });

  public addAttachments(this: ExamStore, fileList: FileList | null, setCurrentProgress: (max: number, now: number, errors: number) => void, setShowAddAttachmentModal: (x: boolean) => void) {
    if (!fileList?.length) {
      setShowAddAttachmentModal(false);
      return;
    }

    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;

    const uploads = [...fileList];
    let max = uploads.length;
    let now = 0;
    let errors = 0;

    const update = async () => {
      if (now === max) {
        setCurrentProgress(-1, -1, -1);
      } else {
        setCurrentProgress(max, now, errors);
      }
      if (now > 0) setShowAddAttachmentModal(false);
      this.sectionUpdateSucceeded(VisitSectionTypes.AttachmentsandConsents);
      await this.getVisit(visitId);
    };
    update();

    const blobStore = new AzureBlobStore({ containerName: BlobEntityType.Attachment });
    for (let i = 0; i < uploads.length; i++) {
      blobStore
        .uploadFile(uploads[i])
        .then(r => {
          return http(`/api/visit/attachment/${visitId}`).post<void>(r);
        })
        .then(() => {
          now++;
          update();
        })
        .catch(() => {
          max -= 1;
          errors += 1;
          update();
        });
    }
  }

  public saveSignatureToVisit = flow(function* (this: ExamStore, formData: WellnessExamForm, image: string, applySignatureToClinic: boolean, signatureSectionVersion: string) {
    if (isUndefined(this.visit)) return;
    if (applySignatureToClinic && !isUndefined(this.visit.veterinarianId)) {
      this.uploadPhoto(image, `signaturePhoto-toClinic-${uuid()}.png`).then((photo: IPhotoRequest) => {
        VeterinarianService.addVeterinarianClinicSignature(this.visit!.veterinarianId!, this.visit!.clinicId, photo);
      });
    }
    const visitId = this.visit.visitId;
    yield VisitService.saveVisitSignature(visitId, image).then((photoModel: PhotoModel) => {
      formData.signature.signatureAppliedToVisit = photoModel;
      this.updateVisitSection(formData, VisitSectionTypes.Signature, signatureSectionVersion);
    });
  });

  saveStoredSignatureToVisitBeforeFinalize = flow(function* (this: ExamStore) {
    let success = false;
    if (isUndefined(this.visit) || (!this.formData!.signature.vetClinicSignature && !this.vetAssignedToVisit?.activeSignature?.signature)) return false;

    const signature = { ...this.formData!.signature, signatureAppliedToVisit: this.formData!.signature.vetClinicSignature || this.vetAssignedToVisit?.activeSignature?.signature };

    yield this.updateVisitSection({ ...this.formData!, signature }, VisitSectionTypes.Signature, this.formData!.signature.sectionRowVersion).then((sectionUpdateResult: SectionUpdateResult) => {
      success = sectionUpdateResult.success;
    });

    return success;
  });

  public removeVeterinarianClinicSignature = flow(function* (this: ExamStore) {
    if (isUndefined(this.visit) || isUndefined(this.visit.veterinarianId)) return;
    yield VeterinarianService.deleteVeterinarianClinicSignature(this.visit.veterinarianId, this.visit.clinicId);
  });

  public removeVisitAttachment = flow(function* (this: ExamStore, visitAttachmentId: number | undefined) {
    if (isUndefined(this.visit) || isUndefined(visitAttachmentId)) return;
    const visitId = this.visit.visitId;
    var success: boolean = yield VisitService.deleteVisitAttachment(visitId, visitAttachmentId)
      .then(() => true)
      .catch(NotificationStoreInstance.onError("Failed to remove visit attachment", false));
    if (success) yield this.getVisit(visitId);
  });

  public uploadPhoto = flow(function* (this: ExamStore, image: string, filename: string) {
    const blobStore = new AzureBlobStore({ containerName: BlobEntityType.Signatures });
    const blobSas: IBlobSasModel = yield blobStore.uploadDataUrl(image, filename, MimeType.PNG);
    const photoRequest: IPhotoRequest = {
      container: blobSas.container,
      fileName: blobSas.fileName,
      photoUrl: blobSas.sasUrl,
    };

    return photoRequest;
  });

  public updateExamResult = flow(function* (this: ExamStore, category: IWellnessExamCategory) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.updateVisitExamResult(visitId, category).then(this.sectionUpdateSucceeded(VisitSectionTypes.ObjectiveWithoutNotes)).then(this.syncVisitSectionVersions);
  });

  public setAllExamResultsToNormal = flow(function* (this: ExamStore) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.setAllResultsToNormal(visitId).then(this.sectionUpdateSucceeded(VisitSectionTypes.ObjectiveWithoutNotes)).then(this.syncVisitSectionVersions);
  });

  public addSalesItemToVisit = flow(function* (this: ExamStore, salesItemOptionId: number) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    const salesItemOptionIdBeingReplaced = this.findSalesItemOptionToReplace(salesItemOptionId);
    this.visit = yield VisitService.addSalesItem(visitId, salesItemOptionId, salesItemOptionIdBeingReplaced).then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder)).then(this.syncVisitSectionVersions).catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to add sales item", this.visit));
  });

  public deleteSalesItemFromVisit = flow(function* (this: ExamStore, salesItemId: number) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    yield VisitService.deleteSalesItem(visitId, salesItemId)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder))
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to remove sales item", this.visit));
  });

  public addSalesPackageToVisit = flow(function* (this: ExamStore, salesPackageId: number) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.setSalesPackage(visitId, salesPackageId).then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder)).then(this.syncVisitSectionVersions).catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales package", this.visit));
  });

  public removeSalesPackageFromVisit = flow(function* (this: ExamStore) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.setSalesPackage(visitId, undefined).then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder)).then(this.syncVisitSectionVersions).catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to remove sales package", this.visit));
  });

  public addSalesPlanToVisit = flow(function* (this: ExamStore, salesPlanId: number) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.addSalesPlan(visitId, salesPlanId).then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder)).then(this.syncVisitSectionVersions).catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales package", this.visit));
  });

  public getAttributeValue = (visitSalesItem: VisitSalesItemModel, name: string): string | undefined => {
    const attr = visitSalesItem.visitSalesItemAttributes.find(vsia => vsia.name === name);
    return attr?.stringValue;
  };
  public setSalesItemAsGiven = flow(function* (this: ExamStore, visitSalesItem: VisitSalesItemModel) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    yield VisitService.updateVisitSalesItemAttributes(visitSalesItem, this.visitIsFinalized)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder))
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales item as given", this.visit));
  });

  // If the sales item has user mark sale item as delined
  public setSalesItemAsDeclined = flow(function* (this: ExamStore, visitSalesItem: VisitSalesItemModel, hospitalId: number, writtenHospitalPrescriptionsEnabled: boolean, isGivenSalesItems?: number[] | undefined) {
    if (isUndefined(this.visit)) return;

    const visitId = this.visit.visitId;

    try {
      // 1. Call backend to mark the item as declined
      yield VisitService.setVisitSalesItemAsDeclined(visitId, visitSalesItem.visitSalesItemId);

      // 2. Mark section update succeeded
      this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder);

      // 3. Refresh visit data
      yield this.getVisit(visitId);

      // 4. Determine whether we need to add a hospital prescription. If is already added as a hospital prescription
      if (!(visitSalesItem.salesItemSalesItemTypeId === SalesItemType.PrescriptionProduct && writtenHospitalPrescriptionsEnabled)
        || this.visit?.hospitalPrescriptions?.some((hp) => hp.salesItemOptionId === visitSalesItem.salesItemOptionId)) return;

      const proactivePrescriptionTraitIds = new Set([Traits.HospitalFleaTickMedication, Traits.HospitalHeartwormFleaTickComboMedication, Traits.HospitalHeartwormMedication]);
      const hasProactivePrescriptionMatchingTraits = visitSalesItem.visitSalesItemTraits.some(trait => proactivePrescriptionTraitIds.has(trait.traitId));

      let prescription: HospitalPrescriptionModel = new HospitalPrescriptionModel({
        petId: this.visit!.petId,
        visitId: this.visit!.visitId,
        salesItemOptionId: visitSalesItem.salesItemOptionId,
        hospitalId: hospitalId
      });

      const rawFills = this.getAttributeValue(visitSalesItem, 'Refills:');
      const frequency = this.getAttributeValue(visitSalesItem, 'Frequency:');
      const dose = this.getAttributeValue(visitSalesItem, 'Dose:');
      const instructions = this.getAttributeValue(visitSalesItem, 'Instructions:');
      const notes = this.getAttributeValue(visitSalesItem, 'Notes:');
      const quantity = this.getAttributeValue(visitSalesItem, 'QTY:');

      const remainingRefills: number | undefined = rawFills ? parseFloat(rawFills) : undefined;

      // From attributes
      prescription.remainingRefills = remainingRefills ?? 0;
      prescription.frequency = frequency;
      prescription.dose = dose;
      prescription.instructions = instructions;
      prescription.notes = notes;
      prescription.quantity = !isGivenSalesItems?.find(salesItemId => salesItemId === visitSalesItem.salesItemId)
        && hasProactivePrescriptionMatchingTraits ? "1" : quantity;
      // Defaults
      if (hasProactivePrescriptionMatchingTraits) {
        prescription.remainingRefills = remainingRefills ?? 12;
        prescription.frequency = frequency || 'monthly';
        prescription.dose = dose || '1';
      }

      // 5. Add hospital prescription if it doesn't exist
      yield this.upsertHospitalPrescription(prescription);

    } catch (error) {
      this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales item as declined", this.visit)(error);
    }
  });


  public setSalesItemAsNotGiven = flow(function* (this: ExamStore, visitSalesItem: VisitSalesItemModel) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    yield VisitService.setVisitSalesItemAsNotGiven(visitId, visitSalesItem.visitSalesItemId)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder))
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales item as declined", this.visit));
  });

  public setSalesItemAsPending = flow(function* (this: ExamStore, visitSalesItem: VisitSalesItemModel) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    yield VisitService.setVisitSalesItemAsPending(visitId, visitSalesItem.visitSalesItemId)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder))
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales item as pending", this.visit));
  });

  public setSalesItemQuantity = flow(function* (this: ExamStore, visitSalesItem: VisitSalesItemModel) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    yield VisitService.setVisitSalesItemQuantity(visitSalesItem)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder))
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to set sales item as declined", this.visit));
  });

  public addVisitDiagnosis = flow(function* (this: ExamStore, diagnosis: DiagnosisModel) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.addVisitDiagnosis(visitId, diagnosis.diagnosisId).then(this.syncVisitSectionVersions).then(this.sectionUpdateSucceeded(VisitSectionTypes.Assessment)).catch(this.sectionUpdateFailed(VisitSectionTypes.Assessment, "Failed to add diagnosis", this.visit));
  });

  public deleteVisitDiagnosis = flow(function* (this: ExamStore, diagnosis: DiagnosisModel) {
    if (isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.deleteVisitDiagnosis(visitId, diagnosis.diagnosisId).then(this.syncVisitSectionVersions).then(this.sectionUpdateSucceeded(VisitSectionTypes.Assessment)).catch(this.sectionUpdateFailed(VisitSectionTypes.Assessment, "Failed to remove diagnosis", this.visit));
  });

  public sendTreatmentPlanSms = flow(function* (this: ExamStore, mobileNumber: string) {
    if (this.visit === undefined || this.visit.visitPublicId === undefined) return;
    const visitId = this.visit.visitId;
    yield VisitService.sendConsentSms(visitId, ConsentType.TreatmentPlanConsent, mobileNumber).then(NotificationStoreInstance.onSuccess("Treatment Consent SMS sent")).catch(NotificationStoreInstance.onError("Failed to send Treatment Consent SMS", {}));
  });

  public signConsent = flow(function* (this: ExamStore, formData: IConsentForm, signaturePhotoDataUrl: string, consentTypePublicId: string) {
    if (this.visit === undefined || this.visit.visitPublicId === undefined || signaturePhotoDataUrl === undefined) return;
    const visitId = this.visit.visitId;
    const visitPublicId = this.visit.visitPublicId;
    const signatureBase64Encoded = signaturePhotoDataUrl.split(",")[1];

    yield VisitService.signAndStoreConsent(signatureBase64Encoded, visitPublicId, consentTypePublicId, false, formData)
      .then(() => this.getVisit(visitId))
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.AttachmentsandConsents, "Signed Consent document is now available"))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to save Consent signature", this.visit));
  });

  public addEstimateToVisit = flow(function* (this: ExamStore, estimate: EstimateModel) {
    if (this.visit === undefined) return;
    const visitId = this.visit.visitId;

    // Attempt to add all estimate items to the visit.
    // Capture any sales items that couldn't be added. These will be listed in a modal.
    // If there are no errors, show a success toast.
    this.addEstimateToVisitErrors = yield PetService.addEstimateToVisit(estimate, visitId)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder, `Added ${estimate.name} to visit`))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to add estimate to visit", []));

    // Refetch the visit to get the latest sales items and pricing.
    yield this.getVisit(visitId).catch(() => NotificationStoreInstance.error("Failed to fetch latest visit information."));
  });

  public updateVisitSectionVersions = flow(function* (this: ExamStore) {
    if (this.visit === undefined) return;
    const latestSectionVersion: VisitSectionVersionModel[] = yield VisitService.getVisitSectionVersions(this.visit.visitId).catch(NotificationStoreInstance.onError("Failed to fetch latest visit section update times.", this.visitSectionVersions));

    if (
      !ObjectUtility.sequenceEquals(
        this.visitSectionVersions.map(vsv => vsv.rowVersion),
        latestSectionVersion.map(vsv => vsv.rowVersion)
      )
    ) {
      yield this.getVisit(this.visit.visitId);
    }
  });

  public createLabOrder = flow(function* (this: ExamStore, ihd: boolean) {
    if (this.visit === undefined) return;

    const visitId = this.visit.visitId;
    yield LabOrderService.createLabOrder(visitId, ihd, this.selectedIvlsDeviceId)
      .then((labOrder: LabOrderModel) => {
        runInAction(() => {
          this.currentLabOrderId = labOrder.labOrderId;
          const index = _.findIndex(this.visitLabOrders, { labOrderId: labOrder.labOrderId });
          if (index !== -1) {
            _.extend(this.visitLabOrders[index], labOrder);
          } else {
            this.visitLabOrders.push(labOrder);
          }
          this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder, "Lab order created");
        });
      })
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to create lab order", this.visit));
  });

  public updateLabOrder = flow(function* (this: ExamStore, labOrderId: number) {
    if (this.visit === undefined) return;

    const visitId = this.visit?.visitId;
    yield LabOrderService.updateLabOrder(labOrderId)
      .then((labOrder: LabOrderModel) => {
        runInAction(() => {
          this.visitLabOrders.map(lo => (lo.labOrderId === labOrder.labOrderId ? labOrder : lo));
          this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder);
        });
      })
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to update lab order", this.visit));
    yield this.getVisit(visitId);
  });

  public cancelLabOrder = flow(function* (this: ExamStore, labOrderId: number) {
    if (this.visit === undefined) return;

    const visitId = this.visit?.visitId;
    yield LabOrderService.cancelLabOrder(labOrderId)
      .then(() => {
        // this.visitLabOrders.map(lo => (lo.labOrderId === labOrder.labOrderId) ? labOrder : lo);
        this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder);
      })
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to cancel lab order", this.visit));
    yield this.getVisit(visitId);
  });

  /**
   * Adds or update a Hospital Prescription
   * @param this current ExamStore reference
   * @param hospitalPrescription HospitalPrescription model to create/update
   */
  public upsertHospitalPrescription = flow(function* (this: ExamStore, hospitalPrescription: HospitalPrescriptionModel) {
    if (this.visit === undefined) return;
    const visitId = this.visit?.visitId;

    yield HospitalService.upsertHospitalPrescription(hospitalPrescription)
      .then(() => {
        this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder);
      })
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to add or update Hospital Prescription", this.visit));
  });

  public deleteHospitalPrescription = flow(function* (this: ExamStore, hospitalPrescriptionId: number) {
    if (this.visit === undefined) return;
    const visitId = this.visit?.visitId;

    yield HospitalService.deleteHospitalPrescription(hospitalPrescriptionId)
      .then(() => {
        this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder);
      })
      .then(() => this.getVisit(visitId))
      .catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to delete Hospital Prescription", this.visit));
  });

  /**
   * Function iterates over each property (exam section) on the WellnessExam Form and looks to see if that section has a followupVisit
   * If there is a followup visit section and there is text in the note section, we add this to an array of modified followupVisitNotes
   * @param formData exam form data to persist
   * @returns parsed followup visit notes added to the form
   */
  private static parseFollowupVisitNotes(formData: WellnessExamForm): VisitNoteModel[] {
    const visitNotes: VisitNoteModel[] = Object.values(formData)
      .filter(value => {
        const followupNote: VisitNoteModel | undefined = get(value, "followupVisit.followupNote");
        return followupNote !== undefined && !isEmpty(followupNote.notes);
      })
      .map(value => {
        return get(value, "followupVisit.followupNote");
      });

    return visitNotes;
  }

  private static ParseVisitUpdateRequest(visit: VisitModel, formData: IWellnessExamForm, visitSectionTypeId: VisitSectionTypes, formSectionVersion: string): VisitUpdateRequest {
    const updatedSection = _.find(visit.visitSectionVersions, { visitSectionTypeId })!;

    return {
      ...visit,
      room: formData.assignment.room,
      dispensingAuthUserId: formData.assignment.dispensingAuthUserId,
      dispensingUserFirstName: formData.assignment.dispensingUserFirstName,
      dispensingUserLastName: formData.assignment.dispensingUserLastName,
      reservationAssignmentStatusId: formData.assignment.reservationAssignmentStatusId,
      reasonForVisit: formData.intake.reasonForVisit,
      appointmentNotes: formData.intake.appointmentNotes,
      veterinarianSignaturePhotoId: formData.signature.signatureAppliedToVisit?.photoId,
      hasVetAttestation: formData.signature.hasVetAttestation,
      appointmentTypeId: formData.assignment.appointmentTypeId,
      visitWellnessExam: WellnessExamForm.ParseUpdateModel(visit.visitWellnessExam, formData),
      updatedSection: { ...updatedSection, rowVersion: formSectionVersion },
    };
  }

  findSalesItemOptionToReplace = (salesItemOptionId: number) => {
    const salesItemOption = find(this.salesItems, { salesItemOptionId });
    if (isUndefined(salesItemOption)) return undefined;
    const salesItemToReplace = find(this.visit?.visitSalesItems, { salesItemId: salesItemOption.salesItemId });
    return salesItemToReplace?.salesItemOptionId;
  };

  public getAnesthesiaMonitoringFormByVisitId = flow(function* (this: ExamStore, visitId: number) {
    this.visitAnesthesiaForms = yield VisitAnesthesiaFormService.getVisitAnesthesiaFormByVisitId(visitId);
  });

  public upsertAnesthesiaMonitoringForm = flow(function* (this: ExamStore, anesthesiaForm: VisitAnesthesiaFormModel) {
    if (anesthesiaForm === undefined) return;
    yield VisitAnesthesiaFormService.upsertVisitAnesthesiaForm(anesthesiaForm).then((data: VisitAnesthesiaFormModel) => {
      this.visitAnesthesiaForms.push(data);
    });
  });

  public upsertVisitAnesthesiaFormPreOp = flow(function* (this: ExamStore, anesthesiaMonitoringFormId: number, anesthesiaFormPreOp: IVisitAnesthesiaFormPreOpModel) {
    if (anesthesiaFormPreOp === undefined || this.visit === undefined || anesthesiaMonitoringFormId === 0) return;

    yield VisitAnesthesiaFormService.upsertVisitAnesthesiaFormPreOp(this.visit?.visitId, anesthesiaMonitoringFormId, anesthesiaFormPreOp)
      .then((data: VisitAnesthesiaFormModel) => {
        this.visitAnesthesiaForms = this.visitAnesthesiaForms.map(item => (item.visitAnesthesiaFormId === data.visitAnesthesiaFormId ? data : item));
      })
      .then(this.anesthesiaSectionUpdateSucceeded(AnesthesiaSectionTypes.PreOp, `${AnesthesiaSectionTypes[AnesthesiaSectionTypes.PreOp]} updated.`))
      .catch(this.anesthesiaSectionUpdateFailed(AnesthesiaSectionTypes.PreOp, `Failed to update ${AnesthesiaSectionTypes[AnesthesiaSectionTypes.PreOp]}`, this.visitAnesthesiaForms[0]));
    return this.anesthesiaSectionUpdateResult;
  });

  @computed get currentVisitAnesthesiaForm(): VisitAnesthesiaFormModel | undefined {
    return this.visitAnesthesiaForms?.length > 0 ? this.visitAnesthesiaForms[0] : undefined;
  }
  //  Computed selector for enabling edit icon on Diagnosis on file table
  @computed get canEditPetDiagnoses(): boolean {
    // normal allowed statuses
    const allowedStatuses = [VisitStatus.In_Progress, VisitStatus.Admitted, VisitStatus.Finalized, VisitStatus.Paid];
    // if visit is completed, allow only if user has BOTH 'Vet' and 'Admin' roles
    if (this.visitIsFinalizedComplete)
      return (this.petDetailsStore.configuration.userHasRole(AuthOperationRoleNames.Admin) || this.petDetailsStore.configuration.userHasRole(AuthOperationRoleNames.Veterinarian));
    // otherwise, normal allowed statuses apply
    return allowedStatuses.includes(this.visit?.visitStatusId!);
  }

  @computed get hasAdminRole(): boolean {
    return this.petDetailsStore.configuration.userHasRole((AuthOperationRoleNames.Admin));
  }
  public deleteAnesthesiaMedication = flow(function* (this: ExamStore, visitAnesthesiaFormPreOpId: number, visitAnesthesiaFormPreOpMedicationId: number) {
    if (this.visit === undefined || visitAnesthesiaFormPreOpId === 0 || visitAnesthesiaFormPreOpMedicationId === 0) return;
    yield VisitAnesthesiaFormService.deleteAnesthesiaMedication(this.visit?.visitId, visitAnesthesiaFormPreOpId, visitAnesthesiaFormPreOpMedicationId)
      .then((data: VisitAnesthesiaFormModel) => {
        this.visitAnesthesiaForms = this.visitAnesthesiaForms.map(item => (item.visitAnesthesiaFormId === data.visitAnesthesiaFormId ? data : item));
      })
      .then(this.anesthesiaSectionUpdateSucceeded(AnesthesiaSectionTypes.PreOp, `Medication deleted`))
      .catch(this.anesthesiaSectionUpdateFailed(AnesthesiaSectionTypes.PreOp, `Failed to delete medication`, this.visitAnesthesiaForms[0]));
  });

  public upsertVisitAnesthesiaFormSurgery = flow(function* (this: ExamStore, anesthesiaMonitoringFormId: number, surgery: IVisitAnesthesiaFormSurgeryModel) {
    if (surgery === undefined || this.visit === undefined || anesthesiaMonitoringFormId === 0) return;

    yield VisitAnesthesiaFormService.upsertVisitAnesthesiaFormSurgery(this.visit?.visitId, anesthesiaMonitoringFormId, surgery)
      .then((data: VisitAnesthesiaFormModel) => {
        this.visitAnesthesiaForms = this.visitAnesthesiaForms.map(item => (item.visitAnesthesiaFormId === data.visitAnesthesiaFormId ? data : item));
      })
      .then(this.anesthesiaSectionUpdateSucceeded(AnesthesiaSectionTypes.Surgery, `${AnesthesiaSectionTypes[AnesthesiaSectionTypes.Surgery]} updated.`))
      .catch(this.anesthesiaSectionUpdateFailed(AnesthesiaSectionTypes.Surgery, `Failed to update ${AnesthesiaSectionTypes[AnesthesiaSectionTypes.Surgery]}`, this.visitAnesthesiaForms[0]));
    return this.anesthesiaSectionUpdateResult;
  });

  public upsertVisitAnesthesiaFormSurgeryVital = flow(function* (this: ExamStore, anesthesiaMonitoringFormId: number, vital: VisitAnesthesiaFormSurgeryVitalModel) {
    if (vital === undefined || this.visit === undefined || anesthesiaMonitoringFormId === 0) return;

    yield VisitAnesthesiaFormService.upsertVisitAnesthesiaFormSurgeryVital(this.visit?.visitId, anesthesiaMonitoringFormId, vital)
      .then((data: VisitAnesthesiaFormModel) => {
        this.visitAnesthesiaForms = this.visitAnesthesiaForms.map(item => (item.visitAnesthesiaFormId === data.visitAnesthesiaFormId ? data : item));
      })
      .then(this.anesthesiaSectionUpdateSucceeded(AnesthesiaSectionTypes.Vital, `${AnesthesiaSectionTypes[AnesthesiaSectionTypes.Vital]} updated.`))
      .catch(this.anesthesiaSectionUpdateFailed(AnesthesiaSectionTypes.Vital, `Failed to update ${AnesthesiaSectionTypes[AnesthesiaSectionTypes.Vital]}`, this.visitAnesthesiaForms[0]));
    return this.anesthesiaSectionUpdateResult;
  });

  public upsertVisitAnesthesiaFormPostSurgery = flow(function* (this: ExamStore, anesthesiaMonitoringFormId: number, postSurgery: IVisitAnesthesiaFormPostSurgeryModel) {
    if (postSurgery === undefined || this.visit === undefined || anesthesiaMonitoringFormId === 0) return;

    yield VisitAnesthesiaFormService.upsertVisitAnesthesiaFormPostSurgery(this.visit?.visitId, anesthesiaMonitoringFormId, postSurgery)
      .then((data: VisitAnesthesiaFormModel) => {
        this.visitAnesthesiaForms = this.visitAnesthesiaForms.map(item => (item.visitAnesthesiaFormId === data.visitAnesthesiaFormId ? data : item));
      })
      .then(this.anesthesiaSectionUpdateSucceeded(AnesthesiaSectionTypes.PostOp, `${AnesthesiaSectionTypes[AnesthesiaSectionTypes.PostOp]} updated.`))
      .catch(this.anesthesiaSectionUpdateFailed(AnesthesiaSectionTypes.PostOp, `Failed to update ${AnesthesiaSectionTypes[AnesthesiaSectionTypes.PostOp]}`, this.visitAnesthesiaForms[0]));
    return this.anesthesiaSectionUpdateResult;
  });

  public upsertVisitAnesthesiaFormPostSurgeryVital = flow(function* (this: ExamStore, anesthesiaMonitoringFormId: number, vital: VisitAnesthesiaFormPostSurgeryVitalModel) {
    if (vital === undefined || this.visit === undefined || anesthesiaMonitoringFormId === 0) return;

    yield VisitAnesthesiaFormService.upsertVisitAnesthesiaFormPostSurgeryVital(this.visit?.visitId, anesthesiaMonitoringFormId, vital)
      .then((data: VisitAnesthesiaFormModel) => {
        this.visitAnesthesiaForms = this.visitAnesthesiaForms.map(item => (item.visitAnesthesiaFormId === data.visitAnesthesiaFormId ? data : item));
      })
      .then(this.anesthesiaSectionUpdateSucceeded(AnesthesiaSectionTypes.Vital, `${AnesthesiaSectionTypes[AnesthesiaSectionTypes.Vital]} updated.`))
      .catch(this.anesthesiaSectionUpdateFailed(AnesthesiaSectionTypes.Vital, `Failed to update ${AnesthesiaSectionTypes[AnesthesiaSectionTypes.Vital]}`, this.visitAnesthesiaForms[0]));
    return this.anesthesiaSectionUpdateResult;
  });

  public updateVisitSalesItemOption = flow(function* (this: ExamStore, oldSalesItemOptionId: number, newSalesItemOptionId: number, reminderDays: number = 0) {
    if (_.isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    this.visit = yield VisitService.updateVisitSalesItemOption(visitId, oldSalesItemOptionId, newSalesItemOptionId, reminderDays).then(this.sectionUpdateSucceeded(VisitSectionTypes.VisitBuilder, "Visit Sales item Updated.")).then(this.syncVisitSectionVersions).catch(this.sectionUpdateFailed(VisitSectionTypes.VisitBuilder, "Failed to update visit sales item.", this.visit));
  });

  @action.bound AddPricePackageRules = () => {
    if (!this.visit || !this.visit?.visitSalesItems || !this.salesItems || this.salesItems.length == 0) return;
    this.visit.visitSalesItems.map(item => {
      const salesItem = this.salesItems.find(x => x.salesItemId == item.salesItemId);
      if (salesItem) {
        const priceRule = salesItem.priceRules?.find(p => defaultTo(p.salesItemOptionId, item.salesItemOptionId) == item.salesItemOptionId);
        item.packageAddOnPrice = priceRule?.packageAddOnPrice;
        item.upgradingPrice = salesItem.priceRules?.find(p => p.salesItemId == salesItem.salesItemUpgradeId)?.price;
      }
    });
  }

  public inactivateVisitSalesItemReminder = flow(function* (this: ExamStore, inactivateVisitReminder: InactivateVisitReminderRequest) {
    yield VisitService.inactivateVisitReminder(inactivateVisitReminder);
  });
  public addDiagnosisUnified = flow(function* (
    this: ExamStore,
    diagnosis: DiagnosisModel,
    allowDiagnosisOnFile: boolean,
  ) {
    if (!this.visit) return;
    const visitId = this.visit.visitId;
    try {
      let latestVisit: VisitModel = this.visit;
      if (allowDiagnosisOnFile) {
        latestVisit = yield VisitService.addPetDiagnosis(
          visitId,
          diagnosis.diagnosisId,
        );
      } else {
        latestVisit = yield VisitService.addVisitDiagnosis(
          visitId,
          diagnosis.diagnosisId,
        );
      }
      this.visit = this.syncVisitSectionVersions(latestVisit);
      this.sectionUpdateSucceeded(VisitSectionTypes.Assessment);
    } catch (error) {
      this.sectionUpdateFailed(
        VisitSectionTypes.Assessment,
        "Failed to add diagnosis",
        this.visit,
      )(error);
    }
  });

  public removeDiagnosisUnified = flow(function* (
    this: ExamStore,
    diagnosis: PetDiagnosisModel | DiagnosisModel,
    allowDiagnosisOnFile: boolean,
  ) {
    if (!this.visit) return;
    const visitId = this.visit.visitId;
    try {
      let latestVisit: VisitModel = this.visit;
      if (allowDiagnosisOnFile) {
        if ("petDiagnosisId" in diagnosis) {
          latestVisit = yield VisitService.deletePetDiagnosis(
            visitId,
            diagnosis.petDiagnosisId,
          );
        }
      } else {
        if ("diagnosisId" in diagnosis) {
          latestVisit = yield VisitService.deleteVisitDiagnosis(
            visitId,
            diagnosis.diagnosisId,
          );
        }
      }
      this.visit = this.syncVisitSectionVersions(latestVisit);
      this.sectionUpdateSucceeded(VisitSectionTypes.Assessment);
    } catch (error) {
      this.sectionUpdateFailed(
        VisitSectionTypes.Assessment,
        "Failed to remove diagnosis",
        this.visit,
      )(error);
    }
  });
  public updateDiagnosisStatus = flow(function* (
    this: ExamStore,
    petDiagnosisUpdateRequest: VisitPetDiagnosisStatusUpdateRequest,
  ) {
    if (!this.visit) return;
    this.visit = yield VisitService.updatePetDiagnosisStatus(petDiagnosisUpdateRequest)
      .then(this.sectionUpdateSucceeded(VisitSectionTypes.Assessment))
      .then(this.syncVisitSectionVersions)
      .catch(
        this.sectionUpdateFailed(VisitSectionTypes.Assessment,
          "Failed to update diagnosis status", this.visit),
      );
  });

  public ensureProactivePrescriptions = flow(function* (this: ExamStore) {
    this.visit = yield VisitService.ensureProactivePrescriptions(this.visit!.visitId);
  });

  public cloneVisitObjectives = flow(function* (this: ExamStore, sourceVisitId: number, notes: string | undefined) {
    if (_.isUndefined(this.visit)) return;
    const visitId = this.visit.visitId;
    const updated = yield VisitService.cloneObjectives(sourceVisitId, visitId, notes);
    this.sectionUpdateSucceeded(VisitSectionTypes.ObjectiveWithoutNotes);
    this.syncVisitSectionVersions(updated);

    runInAction(() => {
      this.visit = updated;
    });
  });
}
